import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PACK_SETS, RoomCodeSchema, type RoomListItem } from '@listen-draw/shared';
import { api, keys, storage } from '../lib/api.js';
import { errorMessage, t, useLocale } from '../lib/i18n.js';
import { track } from '../lib/analytics.js';
import { HeroDemo } from '../components/HeroDemo.js';

export function Landing() {
  useLocale();
  const nav = useNavigate();
  const [rooms, setRooms] = useState<RoomListItem[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [roomsError, setRoomsError] = useState(false);
  const [code, setCode] = useState('');
  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .listRooms()
        .then((r) => {
          if (alive) {
            setRooms(r.rooms);
            setRoomsError(false);
          }
        })
        .catch(() => {
          if (alive) setRoomsError(true);
        });
    void load();
    const timer = setInterval(() => {
      if (!document.hidden) void load();
    }, 10000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);
  const create = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const room = await api.createRoom();
      storage.set(keys.host(room.code), room.hostToken);
      track('room_created', { mode: 'multiplayer' });
      nav(`/host/${room.code}`);
    } catch (e) {
      setError(errorMessage(e instanceof Error ? e.message : 'network'));
    } finally {
      setBusy(false);
    }
  };
  const join = (event: FormEvent) => {
    event.preventDefault();
    const parsed = RoomCodeSchema.safeParse(code.trim());
    if (!parsed.success) {
      setError(
        t(
          'Enter the 4–8 character code from your host.',
          'ホストから教わった4〜8文字の英数字コードを入力してください。',
        ),
      );
      return;
    }
    setError('');
    nav(`/play/${parsed.data}`);
  };
  return (
    <main className="page landing launch-landing">
      <section className="launch-hero">
        <div className="hero-copy">
          <p className="eyebrow">
            <span className="little-dot" />{' '}
            {t('An English listening game', '英語リスニング × ひらめきゲーム')}
          </p>
          <h1>
            {t('Hear the clues.', '聞いて、')}
            <br />
            <span>{t('Beat the picture.', '絵が完成する前に押せ。')}</span>
          </h1>
          <p className="lead">
            {t(
              'Four spoken sentences. One unfolding sketch. Tap your answer the moment it clicks.',
              '4つの英文。少しずつ現れる1枚の絵。どの文の絵かわかった、その瞬間に回答しよう。',
            )}
          </p>
          <div className="hero-actions">
            <Link className="btn btn--primary btn--big" to="/solo">
              {t('Try a round', '一人で遊ぶ')} <span aria-hidden="true">↗</span>
            </Link>
            <a className="text-link" href="#host">
              {t('Play with friends', '友だちと遊ぶ')} →
            </a>
          </div>
          <p className="hero-note">
            {t('♫ Sound on · About a minute · No sign-up', '♫ 音声あり · 約1分 · 登録なし')}
          </p>
        </div>
        <div className="hero-preview">
          <div className="preview-label">
            <span>{t('A round in motion', 'こんなふうに遊びます')}</span>
            <span>{t('PREVIEW', 'プレビュー')}</span>
          </div>
          <HeroDemo />
          <div className="preview-foot">
            {t('The sooner you know, the more you score.', '早く気づくほど、高得点。')}{' '}
            <span aria-hidden="true">↗</span>
          </div>
        </div>
      </section>

      <section className="how-it-clicks" aria-labelledby="how-heading">
        <div className="section-title">
          <h2 id="how-heading">
            {t('A small twist. A big “aha.”', 'ひらめくまでの、3ステップ。')}
          </h2>
          <Link to="/help">{t('The rules', '詳しいルール')} ↗</Link>
        </div>
        <div className="how-cards">
          <article>
            <span className="how-number">
              01 <span aria-hidden="true">♫</span>
            </span>
            <h3>{t('Listen closely.', '耳をすませる。')}</h3>
            <p>
              {t(
                'Hear A, B, C and D. The sentences are hidden, so your ears do the work.',
                'A〜Dの英文を聞こう。文字は伏せられているので、頼りは耳だけ。',
              )}
            </p>
          </article>
          <article>
            <span className="how-number">
              02 <span aria-hidden="true">✎</span>
            </span>
            <h3>{t('Watch it unfold.', '絵が、見えてくる。')}</h3>
            <p>
              {t(
                'A sketch appears, line by line. The tiny differences are your clues.',
                '線が一本ずつ増えていく。小さな違いが、正解につながる手がかり。',
              )}
            </p>
          </article>
          <article>
            <span className="how-number">
              03 <span aria-hidden="true">↗</span>
            </span>
            <h3>{t('Trust your hunch.', 'わかったら、押す。')}</h3>
            <p>
              {t(
                'Choose once. Earlier correct answers earn more; a wrong guess costs 100 points.',
                '回答は一度だけ。早い正解ほど高得点。間違えると100点マイナス。',
              )}
            </p>
          </article>
        </div>
      </section>

      <section className="daily-banner">
        <div>
          <p className="eyebrow">{t('One little daily ritual', '毎日の、耳のウォームアップ')}</p>
          <h2>
            {t('Same clues. A fresh challenge every day.', 'みんな同じ問題で、毎日ひと勝負。')}
          </h2>
          <p>
            {t(
              'See how early you get it. Then send the challenge to a friend.',
              'あなたは何%でわかる？ 結果と挑戦状を友だちに送ろう。',
            )}
          </p>
        </div>
        <Link className="btn btn--primary" to="/daily">
          {t('Play today’s challenge →', '今日の一問に挑戦 →')}
        </Link>
      </section>

      <section id="host" className="host-section">
        <div className="section-title">
          <h2>{t('Better with a little competition.', '友だちとなら、もっと白熱。')}</h2>
          <span className="eyebrow">{t('FRIENDS / CLASSROOMS', '友だち / 教室 / 勉強会')}</span>
        </div>
        <div className="host-cards">
          <article className="card">
            <span className="host-icon" aria-hidden="true">
              ↗
            </span>
            <h3>{t('Bring your people.', 'みんなを招待しよう。')}</h3>
            <p>
              {t(
                'Open a room on your computer. Share the QR code or link. Players answer on their phones — no accounts needed.',
                'PCで部屋を作り、QRやリンクを共有。参加者はスマホで回答できます。登録は不要です。',
              )}
            </p>
            <button className="btn btn--primary" onClick={() => void create()} disabled={busy}>
              {busy ? t('Creating your room…', '作成中…') : t('Host a game', '新しいルームを作る')}
            </button>
            <p className="muted small">
              {t(
                'Free to play · Curated English rounds · Choose your pace',
                '無料で遊べる · オリジナル英文 · 速度を調整可能',
              )}
            </p>
          </article>
          <article className="card join-card">
            <h3>{t('Got a room code?', 'ルームコードをお持ちですか？')}</h3>
            <p>
              {t(
                'Your host has the code. Enter it here and you’re in.',
                'ホストから教わったコードを入力して参加しよう。',
              )}
            </p>
            <form onSubmit={join} className="join-form">
              <label htmlFor="join-code">{t('Room code', 'ルームコード')}</label>
              <div className="row">
                <input
                  id="join-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="ABC23"
                  maxLength={5}
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                />
                <button className="btn btn--primary" disabled={code.trim().length < 5}>
                  {t('Join →', '参加 →')}
                </button>
              </div>
            </form>
            <p className="muted small">
              {t('Or join an open room below.', '下の公開ルームからも参加できます。')}
            </p>
          </article>
        </div>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
      </section>

      <section id="rooms" className="open-rooms">
        <div className="section-title">
          <h2>{t('Open rooms', 'いま開いている部屋')}</h2>
          <span className="muted small">{t('Refreshes automatically', '自動更新')}</span>
        </div>
        {roomsError ? (
          <div className="rooms__empty" role="status">
            {t(
              'Rooms are taking a moment to load. Solo rounds are ready to play.',
              '部屋の読み込みに時間がかかっています。一人用ラウンドで遊べます。',
            )}
            <Link to="/solo" className="btn">
              {t('Try a round', '一人で遊ぶ')}
            </Link>
          </div>
        ) : rooms === null ? (
          <p className="muted" role="status">
            {t('Looking for rooms…', '部屋を探しています…')}
          </p>
        ) : rooms.length === 0 ? (
          <div className="rooms__empty">
            <span>
              {t(
                'A quiet moment. Start the next game?',
                'いまは静かな時間。次のゲームを始めよう。',
              )}
            </span>
            <p className="small">
              {t(
                'Play a solo round now, or invite friends to your own room.',
                'すぐに一人で遊ぶことも、友だちを招待することもできます。',
              )}
            </p>
            <Link className="btn" to="/solo">
              {t('Play solo →', '一人で遊ぶ →')}
            </Link>
          </div>
        ) : (
          <div className="rooms">
            {rooms.map((room) => (
              <div className="room" key={room.code}>
                <span className="room__code">{room.code}</span>
                <div className="room__meta">
                  <span>
                    {room.players} {t('players', '人が参加中')}
                  </span>
                  <span>
                    {room.phase === 'lobby'
                      ? t('Ready to start', '開始待ち')
                      : room.phase === 'results'
                        ? t('Results', '結果発表')
                        : t('In progress · join the next round', '進行中 · 次のラウンドから参加')}
                  </span>
                </div>
                <Link className="btn" to={`/play/${room.code}`}>
                  {t('Join', '参加する')} →
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="landing-faq">
        <h2>{t('A few good things to know.', 'はじめる前に、少しだけ。')}</h2>
        <details>
          <summary>{t('Do I need to draw or speak?', '絵を描いたり、声を出したりする？')}</summary>
          <p>
            {t(
              'Just listen and choose A–D. The game draws the picture. No microphone, drawing skills or account needed.',
              '聞いてA〜Dを選ぶだけ。絵はゲームが描きます。マイク、絵心、アカウントは不要です。',
            )}
          </p>
        </details>
        <details>
          <summary>{t('Is it really free?', '無料で遊べる？')}</summary>
          <p>
            {t(
              'Yes. Solo rounds, the daily challenge and hosting are currently free. There is no checkout or subscription.',
              'はい。一人用、今日の一問、ルーム作成は現在無料です。決済やサブスクリプションはありません。',
            )}
          </p>
        </details>
        <details>
          <summary>{t('What kind of English will I hear?', 'どんな英語が出る？')}</summary>
          <p>
            {t(
              `${PACK_SETS.length.toLocaleString('en-US')} original four-choice questions with recorded audio, based on practical situations at school, home, work, shops and around town.`,
              `学校、家庭、仕事、買い物、地域の施設など、現実的な日常場面を扱う音声付きのオリジナル全${PACK_SETS.length}問です。`,
            )}
          </p>
        </details>
        <details>
          <summary>{t('Can I use it in my class?', '授業で使える？')}</summary>
          <p>
            {t(
              'Yes. Host on a computer, invite learners on their phones, and review the key words together after each round.',
              'はい。PCで部屋を作り、生徒がスマホで参加。答え合わせで決め手の語を一緒に振り返れます。',
            )}{' '}
            <Link to="/help">{t('Read the host guide →', 'ホスト向けガイド →')}</Link>
          </p>
        </details>
      </section>
    </main>
  );
}
